import { useEffect, useState } from 'react';
import { mutations } from 'RestAPIs/rapini';
import type {
  ActivityResponse,
  SuggestedActivitiesGroupResponse
} from 'generated/index';

type UseActivitySuggestionsParams = {
  employeeIds: string[] | undefined;
};

type UseActivitySuggestionsReturn = {
  showOtherActivities: boolean;
  setShowOtherActivities: (show: boolean) => void;
  displayOptions: ActivityResponse[];
  displaySuggestedOptions: ActivityResponse[];
  shouldShowMoreButton: boolean;
  shouldDisableFavoriteFilter: boolean;
};

export const useActivitySuggestions = ({
  employeeIds
}: UseActivitySuggestionsParams): UseActivitySuggestionsReturn => {
  const [showOtherActivities, setShowOtherActivities] = useState(false);
  const [activityGroups, setActivityGroups] = useState<
    SuggestedActivitiesGroupResponse[]
  >([]);

  const { mutate: fetchSuggestedActivities } =
    mutations.useSuggestedActivities();

  useEffect(() => {
    setShowOtherActivities(false);
    fetchSuggestedActivities(
      { employeeIds },
      {
        onSuccess: (data) => setActivityGroups(data ?? []),
        onError: () => setActivityGroups([])
      }
    );
  }, [employeeIds, fetchSuggestedActivities]);

  const suggestedActivities =
    activityGroups.find((g) => g.activityGroupType === 'SUGGESTED')
      ?.activities ?? [];
  const otherActivities =
    activityGroups.find((g) => g.activityGroupType === 'OTHER')?.activities ??
    [];

  const shouldShowMoreButton =
    !!employeeIds?.length && otherActivities.length > 0 && !showOtherActivities;

  return {
    showOtherActivities,
    setShowOtherActivities,
    shouldShowMoreButton,
    displaySuggestedOptions: shouldShowMoreButton ? suggestedActivities : [],
    displayOptions: shouldShowMoreButton
      ? []
      : [...suggestedActivities, ...otherActivities],
    shouldDisableFavoriteFilter: !shouldShowMoreButton
  };
};
